/**
 * ConfigUtils.ts
 * Centralized configuration management for the application
 */

// Declare window.__ENV__ for runtime environment variables
declare global {
  interface Window {
    __ENV__?: {
      REACT_APP_API_URL?: string;
      [key: string]: any;
    };
  }
}

// API URL configuration
// Priority: 1. Runtime env (window.__ENV__), 2. Build-time env (process.env)
// No fallback - API URL must be configured via environment variables
const runtimeApiUrl = typeof window !== 'undefined' ? window.__ENV__?.REACT_APP_API_URL : undefined;
const buildTimeApiUrl = process.env.REACT_APP_API_URL;

export const API_URL = runtimeApiUrl || buildTimeApiUrl;

// Throw error if API_URL is not configured
if (!API_URL) {
  throw new Error(
    'REACT_APP_API_URL is not configured. Please set it in your .env file or build/env.js'
  );
}

// Helper function to get environment variable from runtime or build-time
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  const runtimeValue = typeof window !== 'undefined' ? window.__ENV__?.[key] : undefined;
  const buildTimeValue = process.env[key];
  return runtimeValue || buildTimeValue || defaultValue;
};

// Helper function to get boolean environment variable
const getBooleanEnvVar = (key: string, defaultValue: boolean = false): boolean => {
  const value = getEnvVar(key, '');
  if (value === '') return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

// Other configuration variables can be added here  
export const APP_VERSION = getEnvVar('REACT_APP_VERSION', '1.0.0');

// Debug mode configuration
export const DEBUG_MODE = getBooleanEnvVar('REACT_APP_DEBUG', false);

// API timeout configuration (in milliseconds)
export const API_TIMEOUT = parseInt(getEnvVar('REACT_APP_API_TIMEOUT', '30000'), 10);

// Helper function to check if we're in development mode
export const isDevelopment = process.env.NODE_ENV === 'development';

// Helper function to check if we're in production mode
export const isProduction = process.env.NODE_ENV === 'production';

// Helper function to get environment-specific configuration
export const getConfig = () => {
  return {
    apiUrl: API_URL,
    version: APP_VERSION,
    debugMode: DEBUG_MODE,
    apiTimeout: API_TIMEOUT,
    environment: getEnvVar('NODE_ENV', 'development'),
    isDevelopment,
    isProduction
  };
};

export default {
  API_URL,
  APP_VERSION,
  DEBUG_MODE,
  API_TIMEOUT,
  isDevelopment,
  isProduction,
  getConfig
}; 